// Authentication utilities for validation and state management

// Get user ID from JWT token (more reliable than localStorage)
const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId || payload.id || payload.sub;
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

// Validate authentication state consistency
export const validateAuthState = () => {
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const userId = getUserIdFromToken();
  
  if (!token) {
    console.warn('No authentication token found');
    return false;
  }
  
  if (!userRole) {
    console.warn('No user role found in localStorage');
    return false;
  }
  
  if (!userId) {
    console.warn('No user ID found in token or localStorage');
    return false;
  }
  
  return true;
};

// Sync authentication data (ensure consistency)
export const syncAuthData = () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  
  // Extract user ID from token if not in localStorage
  const tokenUserId = getUserIdFromToken();
  if (tokenUserId && !localStorage.getItem('userId')) {
    localStorage.setItem('userId', tokenUserId.toString());
    console.log('Synced user ID from token to localStorage:', tokenUserId);
  }
  
  // Ensure token is set in axios defaults
  if (typeof window !== 'undefined' && window.axios) {
    window.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

// Enhanced authentication context provider
export const createAuthContext = () => {
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const userId = getUserIdFromToken() || localStorage.getItem('userId');
  const userInfo = localStorage.getItem('userInfo');
  
  return {
    token,
    userRole,
    userId,
    userInfo: userInfo ? JSON.parse(userInfo) : null,
    isAuthenticated: !!token
  };
};

export const authUtils = {
  // Validate if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    return !!(token && userId);
  },

  // Get current user role
  getUserRole() {
    return localStorage.getItem('userRole');
  },

  // Get user info
  getUserInfo() {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  },

  // Get token
  getToken() {
    return localStorage.getItem('token');
  },

  // Check if user has specific role
  hasRole(role) {
    const userRole = this.getUserRole();
    if (!userRole) return false;
    
    const normalizedUserRole = userRole.toLowerCase().trim();
    const normalizedRequiredRole = role.toLowerCase().trim();
    
    return normalizedUserRole === normalizedRequiredRole;
  },

  // Check if user is admin
  isAdmin() {
    return this.hasRole('admin');
  },

  // Check if user is organizer/organiser
  isOrganizer() {
    const userRole = this.getUserRole();
    if (!userRole) return false;
    
    const normalizedRole = userRole.toLowerCase().trim();
    return normalizedRole === 'organizer' || normalizedRole === 'organiser';
  },

  // Get authentication state for debugging
  getAuthState() {
    return {
      isAuthenticated: this.isAuthenticated(),
      token: this.getToken() ? 'exists' : 'missing',
      userId: localStorage.getItem('userId'),
      userRole: this.getUserRole(),
      userInfo: this.getUserInfo(),
      userEmail: localStorage.getItem('userEmail'),
      userName: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType')
    };
  }
};
